import { FeatureName, SupportService } from '../support/support.service';
import { CallStatsService } from '../call-stats/call-stats.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';

export interface MediaDeviceState {
  audioInputId: string;
  videoInputId: string;
  audioOutputId: string;
  roomId: string;
  userId: string;
}

export class MediaDeviceManager {
  constructor(
    private support: SupportService,
    private callstats: CallStatsService,
    private localStorage: LocalStorageService,
    private participantsList: ParticipantsListService,
    private getState: () => MediaDeviceState,
    private setState: (patch: Partial<MediaDeviceState>) => void,
  ) {}

  async setupDefaultIO(): Promise<void> {
    if (!this.support.isFeatureSupported(FeatureName.DeviceConfig)) {
      return;
    }

    const state = this.getState();
    let audioInputId = this.localStorage.getItem('audioInputId');
    let audioOutputId = this.localStorage.getItem('audioOutputId');
    let videoInputId = this.localStorage.getItem('videoInputId');

    let audioInputFound: boolean = audioInputId ? false : true;
    let audioOutputFound: boolean = audioOutputId ? false : true;
    let videoInputFound: boolean = videoInputId ? false : true;

    const devices = await navigator.mediaDevices.enumerateDevices();
    devices.forEach((device: MediaDeviceInfo) => {
      if (device.deviceId !== '') {
        if (device.kind === 'audioinput') {
          if (audioInputId === device.deviceId) {
            audioInputFound = true;
          }
        }
        if (this.support.isFeatureSupported(FeatureName.AudioOutput)) {
          if (device.kind === 'audiooutput') {
            if (audioOutputId === device.deviceId) {
              audioOutputFound = true;
            }
          }
        }
        if (device.kind === 'videoinput') {
          if (videoInputId === device.deviceId) {
            videoInputFound = true;
          }
        }
      }
    });

    if (!audioInputFound) {
      audioInputId = null;
      this.localStorage.removeItem('audioInputId');
    }
    if (!audioOutputFound) {
      audioOutputId = null;
      this.localStorage.removeItem('audioOutputId');
    }
    if (!videoInputFound) {
      videoInputId = null;
      this.localStorage.removeItem('videoInputId');
    }

    this.setState({ audioInputId, audioOutputId, videoInputId });
  }

  updateIO(data: any, outputCb?: boolean): void {
    if (data.data) {
      if (this.support.isFeatureSupported(FeatureName.AudioOutput)) {
        this.localStorage.setItem('audioOutputId', data.data.audioOutputId);
        this.setState({ audioOutputId: data.data.audioOutputId });
        if (outputCb !== false) { this.connectOutput(data.data.audioOutputId); }
      }
      const state = this.getState();
      if (data.data.audioInputId !== state.audioInputId) {
        this.localStorage.setItem('audioInputId', data.data.audioInputId);
        this.setState({ audioInputId: data.data.audioInputId });
      }
      if (data.data.videoInputId !== state.videoInputId) {
        this.localStorage.setItem('videoInputId', data.data.videoInputId);
        this.setState({ videoInputId: data.data.videoInputId });
      }
    }
    const state = this.getState();
    this.callstats.updateUser(state.roomId, state.userId);
  }

  setOutput(id: string, outputId?: string): void {
    const state = this.getState();
    const video = document.getElementById('video-' + id) as HTMLVideoElement;
    if (video && video.setSinkId) {
      if (outputId || state.audioOutputId) {
        video.setSinkId(outputId || state.audioOutputId).catch(() => {});
      }
    } else {
      setTimeout(() => {
        this.setOutput(id, outputId);
      }, 1000);
    }
  }

  async reverseCamera(): Promise<void> {
    const state = this.getState();
    let devices = await navigator.mediaDevices.enumerateDevices();
    devices = devices.filter(device => device.kind === 'videoinput');

    if (devices.length >= 2) {
      if (!state.videoInputId) {
        this.setState({ videoInputId: devices[1].deviceId });
      } else {
        this.setState({
          videoInputId:
            (state.videoInputId === devices[0].deviceId &&
              devices[1].deviceId) ||
            devices[0].deviceId,
        });
      }
    }
  }

  getVideoOptions(): MediaTrackConstraints {
    const state = this.getState();
    const videoOptions: MediaTrackConstraints = {
      width: { ideal: 1280, max: 1280 },
      aspectRatio: { ideal: 16 / 9 },
    };
    if (state.videoInputId) {
      videoOptions.deviceId = { ideal: state.videoInputId };
    }
    return videoOptions;
  }

  getAudioOptions(): MediaTrackConstraints | boolean {
    const state = this.getState();
    let audioOptions: MediaTrackConstraints | boolean = true;
    if (state.audioInputId) {
      audioOptions = {};
      audioOptions.deviceId = { ideal: state.audioInputId };
    }
    return audioOptions;
  }

  connectOutput(outputId: string): void {
    const participants = this.participantsList.participantsArray;
    participants.forEach((participant) => {
      this.setOutput(participant.id, outputId);
    });
  }
}
